/**
 * Tenant Cache Guard (P0 hotfix)
 *
 * Every client-side cache in the app (React Query, IndexedDB jobs cache,
 * job snapshots, drafts, the driver reference pack, and the selected-company
 * key in localStorage) is keyed by job/entity id only — never by tenant.
 * Nothing purged those stores on sign-out, so a device that signed out of
 * company A and into company B could render company A's cached jobs until a
 * fresh server fetch replaced them.
 *
 * This module binds every cache to the authenticated identity
 * (`user_id` + `company_id`). If either changes, every cached store is
 * purged before any UI can read it.
 *
 * Server-side RLS was verified correct and is unchanged — this closes the
 * client cache leak only.
 */

import { appState, jobsCache, jobDrafts, syncQueue } from "./offlineDb";
import { clearDriverPack } from "./driverOfflinePack";
import { queryClient } from "./queryClient";

const OWNER_KEY = "cache_owner_v1";

interface CacheOwner {
  user_id: string;
  company_id: string | null;
}

async function readOwner(): Promise<CacheOwner | null> {
  try {
    const raw = await appState.get(OWNER_KEY);
    return raw ? (JSON.parse(raw) as CacheOwner) : null;
  } catch {
    return null;
  }
}

async function writeOwner(owner: CacheOwner): Promise<void> {
  try {
    await appState.set(OWNER_KEY, JSON.stringify(owner));
  } catch {
    /* ignore */
  }
}

/**
 * Purge every tenant-scoped client cache.
 *
 * The upload queue is cleared too when the identity changes: queued items are
 * signed for the previous account and can never be accepted by the backend
 * under a different identity, so retaining them is pure data leakage.
 * On plain sign-out (same identity may return) the queue is preserved.
 */
export async function purgeTenantCaches(opts: { clearQueue: boolean }): Promise<void> {
  try {
    queryClient.clear();
  } catch {
    /* ignore */
  }

  try {
    localStorage.removeItem("selected_company_id");
  } catch {
    /* ignore */
  }

  await jobsCache.clear().catch(() => undefined);
  await clearDriverPack();

  try {
    const { getAllSnapshots, deleteSnapshot } = await import("./jobSnapshotManager");
    const snapshots = await getAllSnapshots();
    for (const s of snapshots) await deleteSnapshot(s.job_id);
  } catch {
    /* ignore */
  }

  const preserved = opts.clearQueue
    ? new Set<string>()
    : new Set((await syncQueue.getAll().catch(() => [])).map((q) => q.job_id));

  if (opts.clearQueue) {
    await syncQueue.clear().catch(() => undefined);
  }

  const drafts = await jobDrafts.getAll().catch(() => []);
  for (const d of drafts) {
    if (preserved.has(d.job_id)) continue;
    await jobDrafts.delete(d.job_id).catch(() => undefined);
  }

  console.log(
    `[TenantCacheGuard] Caches purged (queue ${opts.clearQueue ? "cleared" : "preserved"})`
  );
}

/**
 * Bind the caches to the signed-in identity. Purges everything when the
 * identity differs from the identity the caches were written for.
 * Returns true when a purge happened.
 */
export async function enforceCacheOwner(
  userId: string,
  companyId: string | null
): Promise<boolean> {
  const current = await readOwner();
  const changed =
    !!current &&
    (current.user_id !== userId ||
      (current.company_id ?? null) !== (companyId ?? null));

  if (changed) {
    console.warn("[TenantCacheGuard] Identity changed — purging cached tenant data");
    await purgeTenantCaches({ clearQueue: true });
  }

  await writeOwner({ user_id: userId, company_id: companyId ?? null });
  return changed;
}

/** Sign-out cleanup: clear cached company data, keep unsynced uploads. */
export async function clearCachesOnSignOut(): Promise<void> {
  await purgeTenantCaches({ clearQueue: false });
  try {
    await appState.delete(OWNER_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Install the guard. Runs before React can render tenant data and on every
 * auth transition (web AND native — the previous cleanup was native-only).
 */
export function installTenantCacheGuard(client: {
  auth: {
    getSession: () => Promise<{ data: { session: { user: { id: string } } | null } }>;
    onAuthStateChange: (cb: (event: string, session: unknown) => void) => unknown;
  };
  from: (t: string) => any;
}): void {
  const resolveCompanyId = async (userId: string): Promise<string | null> => {
    try {
      const { data } = await client
        .from("company_members")
        .select("company_id")
        .eq("user_id", userId)
        .limit(1)
        .maybeSingle();
      return (data?.company_id as string | undefined) ?? null;
    } catch {
      return null;
    }
  };

  const bind = async (userId: string) => {
    const companyId = await resolveCompanyId(userId);
    await enforceCacheOwner(userId, companyId);
  };

  void (async () => {
    try {
      const { data } = await client.auth.getSession();
      if (data.session?.user?.id) await bind(data.session.user.id);
    } catch {
      /* ignore */
    }
  })();

  client.auth.onAuthStateChange((event, session) => {
    const userId = (session as { user?: { id?: string } } | null)?.user?.id;
    if (event === "SIGNED_OUT") {
      void clearCachesOnSignOut();
      return;
    }
    if ((event === "SIGNED_IN" || event === "INITIAL_SESSION") && userId) {
      void bind(userId);
    }
  });
}
